/**
 * OrderDetailDrawer — Modal mit Voll-Daten des Auftrags (UI-05).
 *
 * Layout aus rechnungspruefung-sporttore/SnapshotDrilldown.tsx (1:1):
 *   fixed inset-0 z-50 + bg-black/50 backdrop + 90vh max + flex-1 overflow-auto body.
 *
 * Features:
 *   - Volldaten anzeigen (Adressen, Tags, Bewertung, Items, Timeline) — UI-05
 *   - Offene Notifications mit Aufloesen-Button — UI-11
 *   - Notiz-Feld + Speichern (Backend setzt Audit) — UI-10-Consumer
 *   - Manuell schliessen-Button (Backend setzt closedAt + auto-resolve Notifications) — UI-10-Consumer
 *   - Dirty-Note-Schutz beim Backdrop-Click — RESEARCH Risiko 2
 *   - Race-Condition-Schutz via cancelled-Flag — RESEARCH Risiko 3
 */

import { useCallback, useEffect, useState } from 'react';
import { X, MessageSquare, CheckCircle2, AlertCircle, RefreshCw, Package, Truck } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { OrderListItem } from '../lib/groupOrders';
import TrackingTimeline from './TrackingTimeline';

interface PlentyComment {
  id: number;
  text: string;
  userId: number | null;
  createdAt: string | null;
  isVisibleForContact: boolean;
}

interface OrderDetail {
  order: OrderListItem & {
    billingAddress: Record<string, string | number | null> | null;
    shippingAddress: Record<string, string | number | null> | null;
    tags: Record<string, unknown> | null;
    contactRating: number | null;
    paymentMethodName: string | null;
    orderSource: string | null;
    warenausgangBookedAt: string | null;
    userNoteUpdatedAt: string | null;
    userNoteBy: number | null;
    statusEnteredAt: string | null;
    plentyComments: PlentyComment[] | null;
  };
  items: Array<{
    id: number;
    variationId: string;
    variationNumber: string | null;
    itemName: string | null;
    quantity: number | null;
  }>;
  events: Array<{
    id: number;
    internalStatus: string;
    carrierStatus: string | null;
    eventAt: string;
    eventDescription: string | null;
    eventLocation: string | null;
    carrier: string;
  }>;
  notifications: Array<{
    id: number;
    reason: string;
    triggeredAt: string;
    triggeredStatus: string;
    resolvedAt: string | null;
    resolution: string | null;
  }>;
}

interface OrderDetailDrawerProps {
  orderId: number;
  onClose: () => void;
  onChanged: () => void; // wird vom Parent (MainPage) aufgerufen → re-fetch der Liste
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatAddress(addr: Record<string, string | number | null> | null): string {
  if (!addr) return '–';
  const parts = [
    (addr.name1 ?? addr.fullName) as string | null,
    addr.address1 as string | null,
    [addr.postalCode, addr.town].filter(Boolean).join(' ') || null,
    addr.country as string | null,
  ].filter(Boolean);
  return parts.length > 0 ? (parts as string[]).join(', ') : '–';
}

export default function OrderDetailDrawer({ orderId, onClose, onChanged }: OrderDetailDrawerProps) {
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [closing, setClosing] = useState(false);
  const [resolvingNotifId, setResolvingNotifId] = useState<number | null>(null);

  const isDirty = detail !== null && noteDraft !== (detail.order.userNote ?? '');

  const loadDetail = useCallback(() => {
    setLoading(true);
    setError(null);
    let cancelled = false;
    api
      .get(`/programmes/auftragsverfolgung/detail/${orderId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data as OrderDetail;
        setDetail(data);
        setNoteDraft(data.order.userNote ?? '');
      })
      .catch((err: Error & { response?: { data?: { error?: { message?: string } } } }) => {
        if (cancelled) return;
        setError(err.response?.data?.error?.message ?? err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  useEffect(() => {
    const cleanup = loadDetail();
    return cleanup;
  }, [loadDetail]);

  // ESC-Handler + Dirty-Note-Schutz
  const handleClose = useCallback(() => {
    if (isDirty && !window.confirm('Notiz wurde geaendert. Trotzdem schliessen?')) return;
    onClose();
  }, [isDirty, onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleClose]);

  const handleSaveNote = async () => {
    if (!detail) return;
    setSavingNote(true);
    try {
      await api.post(`/programmes/auftragsverfolgung/notes/${orderId}`, { note: noteDraft });
      onChanged();
      loadDetail();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      window.alert(
        'Notiz konnte nicht gespeichert werden: ' +
          (e.response?.data?.error?.message ?? e.message),
      );
    } finally {
      setSavingNote(false);
    }
  };

  const handleCloseOrder = async () => {
    if (!detail) return;
    if (
      !window.confirm(
        'Auftrag manuell schliessen? Offene Notifications werden ebenfalls aufgeloest.',
      )
    )
      return;
    setClosing(true);
    try {
      await api.post(`/programmes/auftragsverfolgung/close/${orderId}`, { reason: 'manual' });
      onChanged();
      onClose();
    } catch (err: unknown) {
      const e = err as {
        response?: { data?: { error?: { code?: string; message?: string } } };
        message?: string;
      };
      const code = e.response?.data?.error?.code;
      if (code === 'ALREADY_CLOSED') {
        window.alert('Auftrag ist bereits geschlossen.');
      } else {
        window.alert(
          'Fehler beim Schliessen: ' + (e.response?.data?.error?.message ?? e.message),
        );
      }
    } finally {
      setClosing(false);
    }
  };

  const handleResolveNotif = async (notifId: number) => {
    setResolvingNotifId(notifId);
    try {
      await api.post(`/programmes/auftragsverfolgung/notifications/${notifId}/resolve`);
      onChanged();
      loadDetail();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      window.alert(
        'Notification konnte nicht aufgeloest werden: ' +
          (e.response?.data?.error?.message ?? e.message),
      );
    } finally {
      setResolvingNotifId(null);
    }
  };

  const openNotifications = detail?.notifications.filter(n => n.resolvedAt == null) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={handleClose} aria-hidden="true" />

      {/* Modal */}
      <div className="relative z-10 bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#DAD9D9] flex-shrink-0 bg-[#1A1A1A] text-white rounded-t-xl">
          <div>
            <h2 className="font-bold text-base leading-tight">
              {detail ? `Auftrag #${detail.order.plentyOrderId}` : 'Auftrag …'}
            </h2>
            {detail && (
              <div className="text-xs text-gray-300 mt-0.5">
                Eingang {formatDateTime(detail.order.orderReceivedAt)}
                {detail.order.orderType === 4 && ' · LIEFERAUFTRAG'}
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded hover:bg-white/10 transition-colors"
            aria-label="Schliessen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-6">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Lade Detail-Daten…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 p-3 rounded">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {detail && !loading && (
            <>
              {/* Offene Notifications (oben — wichtig) */}
              {openNotifications.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4 text-orange-500" />
                    Offene Alarme ({openNotifications.length})
                  </h3>
                  <ul className="space-y-2">
                    {openNotifications.map(n => (
                      <li
                        key={n.id}
                        className="flex items-center justify-between gap-2 p-2 bg-orange-50 border border-orange-300 rounded"
                      >
                        <div className="text-sm text-orange-900 flex-1">
                          <span className="font-medium">{n.reason}</span>
                          <span className="text-xs text-orange-700 ml-2">
                            seit {formatDateTime(n.triggeredAt)} ({n.triggeredStatus})
                          </span>
                        </div>
                        <button
                          type="button"
                          disabled={resolvingNotifId === n.id}
                          onClick={() => handleResolveNotif(n.id)}
                          className="text-xs px-3 py-1 bg-[#E9FF57] text-[#1A1A1A] font-semibold rounded hover:bg-[#d4ea4d] disabled:opacity-50"
                        >
                          {resolvingNotifId === n.id ? 'Loese auf…' : 'Aufloesen'}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Stammdaten */}
              <section>
                <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">Stammdaten</h3>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-500">Tracking-Status</div>
                    <div className="text-[#1A1A1A] font-medium">{detail.order.trackingStatus}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Plenty-Auftragsstatus</div>
                    <div className="text-[#1A1A1A]">{detail.order.orderStatusName ?? '–'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Bezahlstatus</div>
                    <div className="text-[#1A1A1A]">{detail.order.paymentStatus ?? '–'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Zahlart</div>
                    <div className="text-[#1A1A1A]">{detail.order.paymentMethodName ?? '–'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Quelle</div>
                    <div className="text-[#1A1A1A]">{detail.order.orderSource ?? '–'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Kontaktbewertung</div>
                    <div className="text-[#1A1A1A]">{detail.order.contactRating ?? '–'}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-500 flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      Paketnummer
                    </div>
                    <div className="text-[#1A1A1A] font-mono">
                      {detail.order.packageNumber ?? '–'}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-500 flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      Versandprofil
                    </div>
                    <div className="text-[#1A1A1A]">{detail.order.shippingProfileName ?? '–'}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-500">Warenausgang</div>
                    <div className="text-[#1A1A1A]">
                      {formatDateTime(detail.order.warenausgangBookedAt)}
                    </div>
                  </div>
                </div>
              </section>

              {/* Adressen */}
              <section>
                <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">Adressen</h3>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-500">Rechnungsadresse</div>
                    <div className="text-[#1A1A1A]">
                      {formatAddress(detail.order.billingAddress)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Lieferadresse</div>
                    <div className="text-[#1A1A1A]">
                      {formatAddress(detail.order.shippingAddress)}
                    </div>
                  </div>
                </div>
              </section>

              {/* Artikel */}
              <section>
                <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">
                  Artikel ({detail.items.length})
                </h3>
                {detail.items.length === 0 ? (
                  <div className="text-xs text-gray-500 italic">Keine Positionen.</div>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {detail.items.map(item => (
                      <li
                        key={item.id}
                        className="flex justify-between border-b border-[#DAD9D9] last:border-0 py-1"
                      >
                        <span className="flex-1">
                          {item.itemName ?? 'unbenannt'}
                          {item.variationNumber && (
                            <span className="text-gray-500 ml-2 font-mono">
                              {item.variationNumber}
                            </span>
                          )}
                        </span>
                        <span className="text-gray-600 font-mono">{item.quantity ?? '?'}x</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Timeline */}
              <section>
                <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">
                  Tracking-Verlauf ({detail.events.length})
                </h3>
                <TrackingTimeline events={detail.events} />
              </section>

              {/* Plenty-Notizen (aus Plenty Order Comments) */}
              <section>
                <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2 flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" /> Plenty-Notizen ({detail.order.plentyComments?.length ?? 0})
                </h3>
                {detail.order.plentyComments && detail.order.plentyComments.length > 0 ? (
                  <ul className="space-y-2">
                    {detail.order.plentyComments.map((c) => (
                      <li key={c.id} className="border border-[#DAD9D9] rounded p-2 text-xs bg-gray-50">
                        <div
                          className="text-[#1A1A1A] whitespace-pre-wrap"
                          // Plenty-Notes koennen HTML enthalten (Rich-Text-Editor)
                          dangerouslySetInnerHTML={{ __html: c.text }}
                        />
                        <div className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-2">
                          {c.createdAt && <span>{formatDateTime(c.createdAt)}</span>}
                          {c.userId != null && <span>· User #{c.userId}</span>}
                          {c.isVisibleForContact && (
                            <span className="px-1 rounded bg-blue-50 text-blue-700">Kunde sichtbar</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-gray-500 italic">Keine Notizen in Plenty.</div>
                )}
              </section>

              {/* Lokale Notiz (Libero-intern) */}
              <section>
                <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2 flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" /> Libero-Notiz (intern)
                </h3>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={3}
                  className="w-full text-sm p-2 border border-[#DAD9D9] rounded focus:outline-none focus:border-[#1A1A1A]"
                  placeholder="Interne Notiz fuer dieses Programm…"
                />
                {detail.order.userNoteUpdatedAt && (
                  <div className="text-[10px] text-gray-500 mt-1">
                    Zuletzt aktualisiert {formatDateTime(detail.order.userNoteUpdatedAt)}
                    {detail.order.userNoteBy != null &&
                      ` von User #${detail.order.userNoteBy}`}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleSaveNote}
                  disabled={savingNote || !isDirty}
                  className="mt-2 px-3 py-1.5 text-sm bg-[#E9FF57] text-[#1A1A1A] font-semibold rounded hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingNote ? 'Speichere…' : 'Notiz speichern'}
                </button>
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[#DAD9D9] flex justify-between flex-shrink-0">
          <button
            type="button"
            onClick={handleCloseOrder}
            disabled={
              closing ||
              !detail ||
              ['done', 'closed_manually'].includes(detail.order.trackingStatus)
            }
            className="px-3 py-1.5 text-sm bg-white border border-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <CheckCircle2 className="w-4 h-4" />
            {closing ? 'Schliesse…' : 'Manuell schliessen'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-1.5 text-sm bg-[#E9FF57] text-[#1A1A1A] font-semibold rounded hover:bg-[#d4ea4d]"
          >
            Schliessen
          </button>
        </div>
      </div>
    </div>
  );
}
